<template>
	<div>
		<p
			v-if="booking.is_client_intermediary"
			class="step-title"
		>
			{{ $t("account/bookings.Choose your client") }}
		</p>

		<div
			v-if="booking.is_client_intermediary"
			class="mb-2"
		>
			<div class="row">
				<div class="form-group col-md-8">
					<input-select
						@input="checkInput()"
						:options="booking.clients"
						:placeholder="$t('account/bookings.Select a client')"
						:clearable="false"
						label="company"
						v-model="booking.client"
					/>
				</div>

				<div class="form-group col-md-4">
					<button
						@click="addClient()"
						class="btn btn-neutral w-100"
					>
						{{ $t("account/bookings.Client") }}
					</button>
				</div>
			</div>
		</div>

		<booking-alert
			class="mt-1 mb-5"
			v-if="errors.includes('client')"
		>
			{{ $t("account/bookings.Please select your client") }}
		</booking-alert>

		<p class="step-title"> {{ $t("account/bookings.Choose your service") }} </p>

		<div
			v-if="templates.length"
			class="alert border bg-lighten py-4 my-4 d-flex align-items-center justify-content-center"
		>
			{{ $t("account/bookings.Or use one of my templates") }}

			<button
				@click="$refs.templates.open()"
				class="btn rounded-pill btn-sm btn-primary ml-4"
			>
				{{ $t("account/bookings.My templates") }}
			</button>
		</div>

		<div class="service-items mt-6 mb-5">
			<div
				v-for="key in tabs"
				@click="setService(key)"
				:class="{ active: service == key }"
				class="service-item"
			>
				<div
					v-tippy
					:content="$t('account/bookings.helpers.' + key)"
					class="service-helper"
				>
					<i class="fe fe-help-circle"></i>
				</div>

				<span>
					{{ $t("account/bookings.services." + key) }}
				</span>

				<div
					v-if="getLabel(key)"
					class="badge bg-light small mt-2 px-3 py-2"
				>
					{{ getLabel(key) }}
				</div>
			</div>
		</div>

		<booking-alert
			class="mt-3"
			v-if="errors.includes('service')"
		>
			{{ $t("account/bookings.Please select your service") }}
		</booking-alert>

		<transition name="slide-up">
			<div
				v-if="service"
				class="card card-services bg-lighten shadow-none col-md-12"
			>
				<div class="card-header">
					<p class="h5 text-center">
						{{ $t("account/bookings.Choose your additionals service") }}
					</p>
				</div>

				<div class="card-body">
					<transition-group name="slide-fade">
						<div
							v-if="['customs-sivep', 'customs'].includes(service)"
							key="846A"
							class="service-group"
						>
							<input-checkbox
								:value="services.a846"
								@input="v => setServices('a846', v)"
								class="mb-0"
							>
								<i
									v-tippy
									:content="$t('account/bookings.Certificate 846A')"
									class="bi bi-info-circle mr-3"
								></i>
								{{ $t("account/bookings.846A") }}
							</input-checkbox>
						</div>

						<div
							v-if="['customs-sivep', 'sivep'].includes(service)"
							key="dsce"
							class="service-group"
						>
							<input-checkbox
								:value="services.dsce"
								@input="v => setServices('dsce', v)"
								class="mb-0"
								:label="$t('account/bookings.CHED')"
							/>

							<transition name="slide-fade">
								<div
									v-if="services.dsce"
									class="col-md-5"
								>
									<input-select
										:value="services.dsce_by"
										@input="v => setServices('dsce_by', v)"
										class="mb-0"
										:options="[
											{
												value: 'client',
												label: $t('account/bookings.I do it myself'),
											},
											{
												value: 'asa',
												label: $t('account/bookings.Do it for me'),
											},
										]"
										:clearable="false"
										:reduce="o => o.value"
									/>
								</div>
							</transition>
						</div>

						<div
							v-if="['customs-sivep', 'sivep'].includes(service)"
							key="documentary-control"
							class="service-group"
						>
							<input-checkbox
								class="mb-0"
								:value="services.documentary_control"
								@input="v => setServices('documentary_control', v)"
							>
								<i
									v-tippy
									:content="$t('account/bookings.Thorough control')"
									class="bi bi-info-circle mr-3"
								></i>
								{{ $t("account/bookings.Documentary control") }}
							</input-checkbox>
						</div>

						<div
							key="parking-services"
							v-if="['parking'].includes(service)"
						>
							<div class="service-group">
								<input-checkbox
									:value="services.cts_service == 'simple'"
									@input="v => setServices('cts_service', v ? 'simple' : null)"
									class="mb-0"
								>
									<i
										v-tippy
										:content="$t('account/bookings.Simple desc')"
										class="bi bi-info-circle mr-3"
									></i>
									{{ $t("account/bookings.Simple service") }}
								</input-checkbox>
							</div>

							<div class="service-group">
								<input-checkbox
									:value="services.cts_service == 'complete'"
									@input="v => setServices('cts_service', v ? 'complete' : null)"
									class="mb-0"
								>
									<i
										v-tippy
										:content="$t('account/bookings.Complete desc')"
										class="bi bi-info-circle mr-3"
									></i>
									{{ $t("account/bookings.Complete service") }}
								</input-checkbox>
							</div>

							<div class="service-group">
								<input-checkbox
									:value="services.cts_service == 'transit_clearance'"
									@input="v => setServices('cts_service', v ? 'transit_clearance' : null)"
									class="mb-0"
								>
									<i
										v-tippy
										:content="$t('account/bookings.Clearance transit desc')"
										class="bi bi-info-circle mr-3"
									></i>
									{{ $t("account/bookings.Clearance transit") }}
								</input-checkbox>
							</div>
						</div>
					</transition-group>
				</div>
			</div>
			<!-- /.card -->
		</transition>

		<booking-templates ref="templates" />
	</div>
</template>

<script>
	import { mapState, mapGetters } from "vuex"
	import _ from "lodash"
	import BookingTemplates from "@components/booking/Templates"

	export default {
		data() {
			return {
				errors: [],
				tabs: ["customs", "sivep", "customs-sivep", "parking"],
			}
		},

		components: {
			BookingTemplates,
		},

		computed: {
			...mapState({
				booking: state => state.booking,
				templates: state => state.booking.templates,
			}),

			...mapGetters("booking", ["mandate"]),

			client() {
				return this.booking.client
			},

			service() {
				return this.booking.service
			},

			data() {
				return this.booking.data
			},

			services() {
				return this.booking.services
			},
		},

		methods: {
			checkInput: _.debounce(function () {
				if (this.errors.length) {
					this.checkComplete()
				}
			}, 200),

			setTabs() {
				if (this.mandate === "mandate-simplified") {
					this.tabs = ["sivep", "parking"]
					return
				}

				this.tabs = ["customs", "sivep", "customs-sivep", "parking"]
			},

			checkComplete() {
				var complete = true

				if (!this.client && this.booking.is_client_intermediary) {
					this.errors.push("client")
					complete = false
				}

				if (!this.service) {
					this.errors.push("service")
					complete = false
				}

				if (complete) {
					this.errors = []
				}

				return complete
			},

			getLabel(key) {
				if (["customs", "sivep"].includes(key)) {
					return this.$t("account/bookings.Only")
				}

				if (key == "customs-sivep") {
					return this.$t("account/bookings.Full services")
				}

				return null
			},

			setServices(key, value) {
				this.$store.commit("booking/setServices", {
					key: key,
					value: value,
				})
			},

			setService(key) {
				this.$store.commit("booking/resetServices")
				this.$store.commit("booking/setService", key)
				this.$store.commit("booking/setType", null)
				this.checkComplete()

				if (key == "parking") {
					this.setServices("cts_service", "simple")
					this.$store.commit("booking/setType", "customs-cts")
				}

				var site = this.data.crossing_place

				switch (key) {
					case "customs":
						site = "customs-department"
						break
					case "parking":
						site = "calais-truck-stop"
						break
				}

				this.$store.commit("booking/setSite", site)
			},

			addClient() {
				this.$emit("closed")
				window.location.href = "/account/clients"
			},
		},

		watch: {
			"booking.client": function () {
				this.setTabs()
				this.setService(null)
			},
		},

		mounted() {
			this.setTabs()
			this.setService(null)
		},
	}
</script>
